// ============================================================================
// PROPERTY TERMS SERVICE
// ============================================================================
// Business logic and API calls for Property Terms module

import { get, post, put, del } from '../client/axiosClient';
import { propertyTermsEndpoints } from '../endpoints/propertyTerms.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  PropertyTerms,
  CreatePropertyTermsRequest,
  UpdatePropertyTermsRequest,
  PropertyTermsSearchRequest,
} from '../types';

/**
 * Property Terms Service Class
 */
class PropertyTermsService {
  /**
   * Get all property terms (paginated)
   */
  async getAll(params?: PropertyTermsSearchRequest): Promise<PaginatedResponse<PropertyTerms>> {
    const endpoint = params
      ? buildEndpointWithQuery('/PropertyTerms', params)
      : propertyTermsEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get property terms by ID
   */
  async getById(id: ID): Promise<ApiResponse<PropertyTerms>> {
    const response = await get<ApiResponse<PropertyTerms>>(propertyTermsEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new property terms
   */
  async create(data: CreatePropertyTermsRequest): Promise<ApiResponse<PropertyTerms>> {
    const response = await post<ApiResponse<PropertyTerms>>(
      propertyTermsEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update property terms
   */
  async update(id: ID, data: UpdatePropertyTermsRequest): Promise<ApiResponse<PropertyTerms>> {
    const response = await put<ApiResponse<PropertyTerms>>(
      propertyTermsEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete property terms
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(propertyTermsEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const propertyTermsService = new PropertyTermsService();
export default propertyTermsService;
